// 微電網資料的斷線判定。
//
// 設備通訊中斷時，後端寫入的是 0 而不是空值，因此「全部為 0」與「真的都是零」
// 在資料上長得一樣。判斷依據是物理上的不可能：電池電量不會是 0%，整棟建物的
// 負載也不會是 0 W。六個瞬時欄位同時為 0 只可能是沒有讀到資料。
//
// 反過來說，真實的零值不會誤判：夜間太陽能為 0 時，電池電量仍是 90.8、
// 負載仍有數百瓦，不會六個同時為 0。

// 瞬時量測值。斷線時一律歸零，故用這六個判斷。
const INSTANT_FIELDS = [
    'pvpower',
    'gridpower',
    'loadmeteractivepower',
    'batterysoc',
    'chargingpower',
    'dischargingpower',
]

// 每日累積量。這兩個是設備存著的總數，斷線時不會歸零而是停在最後一個值
// （實測 8/20 斷線期間 todayloadpower 連續 56 筆都是 1.23），
// 因此不能列入斷線判斷，否則永遠判不出斷線。
const ACCUMULATED_FIELDS = ['todaypvpower', 'todayloadpower']

export const MICROGRID_FIELDS = [...INSTANT_FIELDS, ...ACCUMULATED_FIELDS]

// 站點若排除了微電網卡片，這些欄位根本不會被抓取。
// 此時「全部為 0」的條件會意外成立，故先確認資料裡真的有這些欄位。
function hasMicrogridFields(record) {
    return INSTANT_FIELDS.some((field) => record?.[field] !== undefined)
}

/**
 * 判斷單筆資料是否為設備斷線。
 *
 * @param {Object} record 含微電網欄位的單筆資料
 * @returns {boolean} 六個瞬時欄位皆為 0 或空值時為 true
 */
export function isMicrogridOffline(record) {
    if (!hasMicrogridFields(record)) return false
    return INSTANT_FIELDS.every((field) => {
        const value = record[field]
        return value === 0 || value === null || value === undefined
    })
}

/**
 * 把欄位導向資料中斷線的樣本改為 null。
 *
 * 保留 0 會讓折線圖畫出一條貼著底線的假資料，改成 null 才會斷開，
 * 讀者一眼看得出那段沒有資料。
 *
 * @param {Object} grouped 欄位導向的歷史資料，每個欄位一個陣列
 * @returns {Object} 同一個物件（就地修改）
 */
export function maskOfflineSamples(grouped) {
    const length = grouped?.['DateTime']?.length ?? 0
    if (!length || !hasMicrogridFields(sampleAt(grouped, 0))) return grouped

    for (let i = 0; i < length; i++) {
        if (!isMicrogridOffline(sampleAt(grouped, i))) continue
        MICROGRID_FIELDS.forEach((field) => {
            if (Array.isArray(grouped[field])) grouped[field][i] = null
        })
    }
    return grouped
}

/**
 * 從欄位導向資料中找出最後一筆有讀到資料的時間。
 *
 * 資料視窗只涵蓋最近數小時，若設備已中斷更久，視窗內可能一筆都沒有，
 * 此時回傳 null，由畫面決定只顯示「中斷」而不附時間。
 *
 * @param {Object} grouped 欄位導向的歷史資料
 * @returns {Date|null} 最後一筆有資料的時間
 */
export function findLastOnlineTime(grouped) {
    const dateTimeList = grouped?.['DateTime']
    if (!Array.isArray(dateTimeList)) return null

    for (let i = dateTimeList.length - 1; i >= 0; i--) {
        if (isMicrogridOffline(sampleAt(grouped, i))) continue
        const value = dateTimeList[i]
        return value instanceof Date ? value : new Date(value)
    }
    return null
}

// 把欄位導向資料的第 i 筆組回單筆物件，好共用上面的判斷式
function sampleAt(grouped, index) {
    const record = {}
    MICROGRID_FIELDS.forEach((field) => {
        if (Array.isArray(grouped?.[field])) record[field] = grouped[field][index]
    })
    return record
}
